import { useMemo, useState, type ReactNode } from "react";
import { AlarmClock, AlarmClockOff, Bell, CalendarDays, Copy, Flag, Repeat, RotateCcw, Star, Tag, Trash2 } from "lucide-react";
import { PRIORITY_LABEL, STATUS_LABEL, type TaskWithFlags } from "@desk/contracts";
import type { TaskDetailParts } from "@/concepts/types";
import { DropdownMenu, DropdownMenuContent, DropdownMenuSeparator, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DatePicker } from "@/components/ui/date-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip } from "@/components/ui/misc";
import { Button } from "@/components/ui/button";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { useHierarchy, useTaskList } from "@/data/hooks";
import { useTaskActions } from "@/data/actions";
import { useOutbox, outbox } from "@/data/outbox";
import { deadlineState, formatDay, formatInstant, formatTimestamp, localDateOf } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { chipClass } from "@/features/quick-add/QuickAdd";
import { DestIcon, DestinationPicker, destPath, normalizeDest, sameDest, type Dest } from "@/features/destination/DestinationPicker";
import { deadlineText } from "@/components/task-bits";
import type { Hierarchy } from "@/data/hooks";
import { PriorityRadioItems, StatusRadioItems, TaskMenu } from "./TaskMenu";
import { ChecklistEditor, NotesEditor, TitleEditor } from "./editors";
import { ReminderPicker } from "./ReminderPicker";
import { RepeatPicker } from "./RepeatPicker";
import { repeatLabel } from "@/lib/repeat";
import { CustomSnoozeDialog, SnoozePresetItems } from "@/features/snooze/SnoozeControls";
import { copyText } from "@/lib/clipboard";

/** "HumVision › Windows release" (area only when filed directly in it). */
function destShort(d: Dest, h: Hierarchy) {
  const s = h.space(d.spaceId)?.name;
  const p = h.project(d.projectId)?.name;
  if (s) return p ? `${s} › ${p}` : s;
  return h.area(d.areaId)?.name ?? "Inbox";
}

/** Detail chips are borderless until hovered so the card stays quiet. */
const chip = cn(chipClass, "border-transparent bg-transparent data-[empty=true]:border-transparent hover:bg-hover");

/** Find, toggle, or create labels for a task, in a popover opened by `children`. */
export function LabelsPopover({ task, children }: { task: TaskWithFlags; children: ReactNode }) {
  const a = useTaskActions();
  const [draft, setDraft] = useState("");
  // Same query as the sidebar counts, so suggestions share that cached list.
  const { tasks } = useTaskList({ includeClosed: true, includeSnoozed: true, limit: 500, sort: "created" });
  const known = useMemo(() => {
    const set = new Set<string>();
    tasks.forEach((t) => t.labels.forEach((l) => set.add(l)));
    return [...set].sort((x, y) => x.localeCompare(y));
  }, [tasks]);
  const toggle = (label: string) => {
    const has = task.labels.includes(label);
    void a.update(task, { labels: has ? task.labels.filter((l) => l !== label) : [...task.labels, label] });
  };
  const options = [...new Set([...known, ...task.labels])].filter((l) => !draft || l.toLowerCase().includes(draft.toLowerCase()));
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[240px] p-1.5" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <input
          aria-label="Find or create label"
          placeholder="Label"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim()) {
              e.preventDefault();
              const v = draft.trim().slice(0, 40);
              if (!task.labels.includes(v)) toggle(v);
              setDraft("");
            }
          }}
          className="mb-1 h-8 w-full rounded-md border border-input bg-card px-2 text-[13.5px] outline-none"
        />
        <div className="max-h-56 overflow-y-auto">
          {options.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => toggle(l)}
              className="flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[13.5px] hover:bg-hover"
            >
              <span
                className={cn(
                  "inline-flex size-3.5 items-center justify-center rounded-[3px] border",
                  task.labels.includes(l) ? "border-foreground bg-foreground" : "border-foreground/30",
                )}
              />
              <span className="truncate">{l}</span>
            </button>
          ))}
          {draft.trim() && !options.some((l) => l.toLowerCase() === draft.trim().toLowerCase()) && (
            <div className="px-2 py-1.5 text-[12.5px] text-muted-foreground">Press Enter to add “{draft.trim()}”</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function LabelsEditor({ task }: { task: TaskWithFlags }) {
  return (
    <LabelsPopover task={task}>
      <button type="button" className={chip} data-empty={task.labels.length === 0} aria-label={task.labels.length ? `Labels: ${task.labels.join(", ")}` : "Add labels"}>
        <Tag />
        <span className="chip-text truncate">{task.labels.length ? task.labels.join(", ") : "Labels"}</span>
      </button>
    </LabelsPopover>
  );
}

function SnoozeChip({ task }: { task: TaskWithFlags }) {
  const a = useTaskActions();
  const { timezone } = useHierarchy();
  const [custom, setCustom] = useState(false);
  const snoozed = task.flags.isSnoozed && task.snoozedUntil;
  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button type="button" className={chip} data-empty={!snoozed} aria-label={snoozed ? "Snoozed" : "Snooze"}>
            <AlarmClock />
            <span className="chip-text">{snoozed ? `Until ${formatInstant(task.snoozedUntil!, timezone)}` : "Snooze"}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent onKeyDown={(e) => e.stopPropagation()}>
          <SnoozePresetItems onPick={(iso) => void a.snooze(task, iso)} onCustom={() => setCustom(true)} />
          {snoozed && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => void a.unsnooze(task)}>
                <AlarmClockOff />
                Unsnooze
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <CustomSnoozeDialog open={custom} onOpenChange={setCustom} onConfirm={(iso) => void a.snooze(task, iso)} />
    </>
  );
}

export function SyncNotice({ taskId }: { taskId: string }) {
  const { entries } = useOutbox();
  const mine = entries.filter((e) => ("taskId" in e.op ? outbox.resolve(e.op.taskId) === taskId : e.op.type === "task.create" && e.op.input.clientId === taskId));
  const failed = mine.find((e) => e.state === "failed");
  const pending = mine.some((e) => e.state === "pending" || e.state === "sending");
  if (failed) {
    return (
      <div role="alert" className="mb-2 flex items-center gap-2 rounded-md bg-urgent-soft px-2.5 py-1.5 text-[13px]">
        <span className="flex-1">Sync failed: {failed.error}</span>
        <Button size="xs" variant="outline" onClick={() => void outbox.retry(failed.id)}>
          Retry
        </Button>
        <Button size="xs" variant="quiet" onClick={() => void outbox.discard(failed.id)}>
          Discard change
        </Button>
      </div>
    );
  }
  if (pending) return <p className="mb-1 text-[12.5px] text-subtle">Pending sync</p>;
  return null;
}

export function useTaskDetailParts(task: TaskWithFlags | undefined, opts: { autoFocusTitle?: boolean } = {}): TaskDetailParts | null {
  const h = useHierarchy();
  const a = useTaskActions();
  if (!task) return null;
  const closed = task.status === "done" || task.status === "cancelled";
  const dest = normalizeDest(task, h);
  const planned = task.plannedDate;
  const dl = task.deadline ? deadlineState(task.deadline, h.today) : null;

  return {
    title: <TitleEditor task={task} autoFocus={opts.autoFocusTitle} />,
    notes: <NotesEditor task={task} />,
    checklist: <ChecklistEditor task={task} />,
    syncNotice: <SyncNotice taskId={task.id} />,
    properties: {
      when: (
        <DatePicker value={planned} today={h.today} onChange={(d) => void a.setWhen(task, d)} clearLabel="No date">
          <button type="button" className={chip} data-empty={!planned} aria-label={planned ? `When: ${formatDay(planned, h.today)}` : "Set when"}>
            {planned === h.today ? <Star className="fill-[var(--star)] !text-[var(--star)]" /> : <CalendarDays />}
            <span className="chip-text">{planned ? formatDay(planned, h.today) : "When"}</span>
          </button>
        </DatePicker>
      ),
      deadline: (
        <DatePicker value={task.deadline} today={h.today} onChange={(d) => void a.update(task, { deadline: d })} clearLabel="No deadline">
          <button
            type="button"
            className={cn(chip, dl === "overdue" && "text-overdue", dl === "today" && "text-due")}
            data-empty={!task.deadline}
            aria-label={task.deadline ? `Deadline ${task.deadline}` : "Set deadline"}
          >
            <Flag />
            <span className="chip-text">{task.deadline ? deadlineText(task.deadline, h.today) : "Deadline"}</span>
          </button>
        </DatePicker>
      ),
      priority: (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button type="button" className={chip} data-empty={task.priority === "none"} aria-label={`Priority: ${PRIORITY_LABEL[task.priority]}`}>
              <PriorityIcon priority={task.priority} />
              <span className="chip-text">{task.priority === "none" ? "Priority" : PRIORITY_LABEL[task.priority]}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent onKeyDown={(e) => e.stopPropagation()}>
            <PriorityRadioItems value={task.priority} onChange={(p) => void a.setPriority(task, p)} />
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      status: (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button type="button" className={chip} aria-label={`Status: ${STATUS_LABEL[task.status]}`}>
              <StatusIcon status={task.status} />
              {STATUS_LABEL[task.status]}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent onKeyDown={(e) => e.stopPropagation()}>
            <StatusRadioItems value={task.status} onChange={(s) => void a.setStatus(task, s)} />
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      location: (
        <DestinationPicker value={dest} onChange={(d) => !sameDest(d, dest) && void a.move(task, d)}>
          <button type="button" className={chip} aria-label={`Location: ${destPath(dest, h)}`}>
            <DestIcon d={dest} />
            <span className="truncate">{destShort(dest, h)}</span>
          </button>
        </DestinationPicker>
      ),
      reminder: (
        <ReminderPicker value={task.reminderAt} onChange={(iso) => void a.update(task, { reminderAt: iso })}>
          <button type="button" className={chip} data-empty={!task.reminderAt} aria-label={task.reminderAt ? "Reminder" : "Add reminder"}>
            <Bell />
            <span className="chip-text">{task.reminderAt ? formatInstant(task.reminderAt, h.timezone) : "Remind"}</span>
          </button>
        </ReminderPicker>
      ),
      repeat: (
        <RepeatPicker value={task.repeat} onChange={(rule) => void a.update(task, { repeat: rule })}>
          <button type="button" className={chip} data-empty={!task.repeat} aria-label={task.repeat ? `Repeat: ${repeatLabel(task.repeat)}` : "Set repeat"}>
            <Repeat />
            <span className="chip-text">{task.repeat ? repeatLabel(task.repeat) : "Repeat"}</span>
          </button>
        </RepeatPicker>
      ),
      labels: <LabelsEditor task={task} />,
      snooze: closed ? null : <SnoozeChip task={task} />,
    },
    actions: (
      <>
        {!closed && (
          <Tooltip content={planned === h.today ? "Remove from Today" : "Add to Today"} shortcut="T">
            <Button
              size="icon"
              variant="quiet"
              aria-pressed={planned === h.today}
              aria-label={planned === h.today ? "Remove from Today" : "Add to Today"}
              onClick={() => void a.toggleToday(task)}
            >
              <Star className={cn(planned === h.today && "fill-[var(--star)] !text-[var(--star)]")} />
            </Button>
          </Tooltip>
        )}
        {task.deletedAt ? (
          <Button size="sm" variant="outline" onClick={() => void a.restore(task)}>
            <RotateCcw />
            Restore
          </Button>
        ) : (
          <Tooltip content="Delete" shortcut="⌘⌫">
            <Button size="icon" variant="quiet" aria-label="Delete task" onClick={() => void a.remove(task)}>
              <Trash2 />
            </Button>
          </Tooltip>
        )}
        <TaskMenu task={task} />
      </>
    ),
    meta: (
      <>
        <button
          type="button"
          onClick={() => {
            void copyText(task.readableId, `Copied ${task.readableId}`);
          }}
          className="inline-flex items-center gap-1 rounded px-1 font-medium tabular text-muted-foreground hover:bg-hover hover:text-foreground"
          aria-label={`Copy ${task.readableId}`}
        >
          {task.readableId === "DSK-0" ? "Not synced yet" : task.readableId}
          <Copy className="size-3" />
        </button>
        <span title={formatTimestamp(task.createdAt, h.timezone)}>Created {formatDay(localDateOf(task.createdAt, h.timezone), h.today)}</span>
        <span title={formatTimestamp(task.updatedAt, h.timezone)}>Updated {formatInstant(task.updatedAt, h.timezone)}</span>
        {task.completedAt && closed && <span>Completed {formatInstant(task.completedAt, h.timezone)}</span>}
      </>
    ),
  };
}
